// Anti-farming threat (Phase 2): running a noisy search at a POI leaves a decaying "threat"
// signature on it. Revisit too soon and it's hotter — more enemies, and a
// higher chance of the (deferred) travel-interrupt encounter. The point is to
// make spreading supply runs across POIs the correct play and re-hitting one POI a
// deliberately worse option, not to punish the player arbitrarily
// (GAME_IDENTITY_V4.md new pillar 9).
//
// Decay is lazy: threat is stored as a value stamped with the day it was set,
// and the current level is computed on read by subtracting decay for the days
// elapsed. That means no daily-tick mutation is required for correctness (the
// app can still call decayThreat() on the daily tick to materialise/prune it,
// but nothing breaks if it doesn't). This module is pure and wires into
// nothing yet — threatToEnemyCount / threatToInterruptChance are exposed as
// outputs the tactical-zone builder can consume later.

export const THREAT_PER_VISIT = 1; // fallback when a supply run reports no accumulated threat
export const THREAT_DECAY_PER_DAY = 1; // a POI sheds ~1 threat/day (design: "decays by 1")
export const THREAT_MAX = 12; // room for cumulative supply-run threat; a couple of loud runs saturate it
export const BASE_ENEMY_COUNT = 3;
export const ENEMIES_PER_THREAT = 1 / 3; // ~1 extra patrol per 3 threat (saturated POI ≈ +4)
export const MAX_INTERRUPT_CHANCE = 0.6;

// --- Supply-run threat generation (v4 threat system) ---------------------------
// Noisy tactical actions leave a signature. These are the discrete events the
// turn resolver rolls/emits (see tactical-turn.mjs); their sum becomes the
// supply run's accumulated threat, applied to the POI + the global gauge on extract.
export const THREAT_PER_SCAVENGE = 1;
export const SEARCH_THREAT_CHANCE = 0.34; // ~"1-in-3" chance a searched item is noisy (shown in the scavenge menu)
export const THREAT_PER_DETECTION = 1; // an enemy spotting you (entering pursuit)
export const THREAT_TURN_INTERVAL = 10; // lingering costs: every Nth turn...
export const THREAT_PER_TURN_TICK = 1; // ...adds this much threat regardless of action
// Reinforcements arrive per this many points of accumulated threat crossed (not
// one per point) — so noise ramps pressure gradually, not one enemy per tick.
export const THREAT_PER_REINFORCEMENT = 5;

// Whether a single searched item comes up noisy. Pure: caller supplies the rng
// (Math.random in the app, a seeded stub in tests).
export function rollSearchThreat(rng = Math.random) {
  return rng() < SEARCH_THREAT_CHANCE;
}

// --- Global (campaign-wide) threat ----------------------------------------
// The neighborhood-scale pressure gauge. Every supply run's accumulated threat also
// feeds this; it decays slowly day to day. Crossing GLOBAL_THREAT_THRESHOLD
// latches `thresholdReached` — the signal a future "the base is found" event
// will consume (not built yet; for now we just track and surface it).
export const GLOBAL_THREAT_THRESHOLD = 100;
export const GLOBAL_THREAT_DECAY_PER_DAY = 2; // slower than a POI cools — a real gauge, not a per-block value

export function createGlobalThreat() {
  return { value: 0, thresholdReached: false };
}

export function normalizeGlobalThreat(globalThreat) {
  return {
    value: Number.isFinite(globalThreat?.value) ? Math.max(0, globalThreat.value) : 0,
    thresholdReached: Boolean(globalThreat?.thresholdReached),
  };
}

// Add threat (a supply run's accumulated total), latching the threshold flag once the
// running value crosses it. Never mutates the input.
export function addGlobalThreat(globalThreat, amount, threshold = GLOBAL_THREAT_THRESHOLD) {
  const base = normalizeGlobalThreat(globalThreat);
  const value = Math.max(0, base.value + (Number.isFinite(amount) ? amount : 0));
  return { value, thresholdReached: base.thresholdReached || value >= threshold };
}

// Slow daily cooldown. The threshold latch persists through decay so a crossed
// threshold stays "armed" for its (deferred) consumer even as the gauge falls.
export function decayGlobalThreat(globalThreat, perDay = GLOBAL_THREAT_DECAY_PER_DAY) {
  const base = normalizeGlobalThreat(globalThreat);
  return { value: Math.max(0, base.value - perDay), thresholdReached: base.thresholdReached };
}

export function hasReachedGlobalThreshold(globalThreat, threshold = GLOBAL_THREAT_THRESHOLD) {
  return normalizeGlobalThreat(globalThreat).value >= threshold;
}

function entry(threatState, poiId) {
  return threatState?.[poiId] ?? null;
}

// Current threat at a POI, after lazily decaying from its last-visited day.
export function poiThreat(threatState, poiId, currentDay) {
  const record = entry(threatState, poiId);
  if (!record) return 0;
  const daysElapsed = Math.max(0, currentDay - (record.lastVisitedDay ?? currentDay));
  const decayed = record.value - THREAT_DECAY_PER_DAY * daysElapsed;
  return Math.max(0, decayed);
}

// Record a supply run on a POI: add THREAT_PER_VISIT on top of whatever threat remained
// after decay, clamped to THREAT_MAX, stamped with the current day. Returns a new
// threat state (never mutates the input).
export function recordPoiVisit(threatState, poiId, currentDay, amount = THREAT_PER_VISIT) {
  const current = poiThreat(threatState, poiId, currentDay);
  return {
    ...threatState,
    [poiId]: { value: Math.min(THREAT_MAX, current + amount), lastVisitedDay: currentDay },
  };
}

// Materialise decay into stored values and drop fully-cooled POIs, for callers
// that want to prune the state on the daily tick rather than rely on lazy read.
export function decayThreat(threatState, currentDay) {
  const next = {};
  for (const poiId of Object.keys(threatState ?? {})) {
    const value = poiThreat(threatState, poiId, currentDay);
    if (value > 0) next[poiId] = { value, lastVisitedDay: currentDay };
  }
  return next;
}

// Effective threat at a point: threat fills the whole supply-run radius, not just the one
// searched building. A supply run leaves its threat on the searched POI (one stored value,
// one circle drawn); any point that circle covers — including neighboring POIs
// inside it — reads that threat here. `poiEntries` is [{ coordinate, threat }] of
// the currently-hot POIs; `distanceFn(a, b)` returns meters. Returns the max
// over every hot circle covering the point (an unthreated point reads 0).
export function areaThreatAt(point, poiEntries, radiusMeters, distanceFn) {
  let threat = 0;
  for (const entry of poiEntries ?? []) {
    if (!(entry?.threat > 0) || !Array.isArray(entry.coordinate)) continue;
    if (distanceFn(point, entry.coordinate) <= radiusMeters) threat = Math.max(threat, entry.threat);
  }
  return threat;
}

// How many patrols a zone should spawn given its current threat — the tactical
// zone builder can consume this once threat feeds spawns (not wired yet).
export function threatToEnemyCount(threat, { baseCount = BASE_ENEMY_COUNT, perThreat = ENEMIES_PER_THREAT } = {}) {
  return baseCount + Math.round(Math.max(0, threat) * perThreat);
}

// Probability of a forced travel-interrupt encounter near a hot POI, ramping
// with threat toward MAX_INTERRUPT_CHANCE. (The interrupt itself is a deferred
// stretch feature; this is the knob it will read.)
export function threatToInterruptChance(threat) {
  const normalized = Math.min(1, Math.max(0, threat) / THREAT_MAX);
  return normalized * MAX_INTERRUPT_CHANCE;
}
